import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { downloadCsv } from '../helpers';
import { ImpactBadge, CsvButton, WhyThisMatters } from '../components/Shared.jsx';

export function KeywordAnalyzer({ data }) {
  const { coreKeywords = [], discoveryKeywords = [], keywordGaps = [], actionableSuggestions = [], summary } = data;

  const handleCsvExport = () => downloadCsv(
    'keyword-analysis.csv',
    ['Type', 'Keyword', 'Strength/Opportunity', 'Appearances', 'Context/Reasoning'],
    [
      ...coreKeywords.map((k) => ['Core', k.keyword, k.strength, k.appearances, k.context]),
      ...discoveryKeywords.map((k) => ['Discovery', k.keyword, k.opportunity, '', k.reasoning]),
      ...keywordGaps.map((k) => ['Gap', k.gap, k.impact, '', `Found in: ${k.foundIn?.join(', ')} | Missing from: ${k.missingFrom?.join(', ')}`]),
    ]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <CsvButton onClick={handleCsvExport} />
      </Box>

      {summary && (
        <Box sx={{ mb: 3, p: 2, background: 'rgba(45,212,191,0.1)', border: `1px solid rgba(45,212,191,0.3)`, borderRadius: 1.5 }}>
          {typeof summary === 'string'
            ? <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: T.text }}>{summary}</Typography>
            : <>
                {summary.overall_discoverability && <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: T.text }}>{summary.overall_discoverability}</Typography>}
                {summary.primary_gap && <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.5 }}>{summary.primary_gap}</Typography>}
              </>
          }
        </Box>
      )}

      {coreKeywords.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.success, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>✓ Core Keywords</Typography>
          {coreKeywords.map((kw, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(34,197,94,0.07)', border: `1px solid rgba(34,197,94,0.25)`, borderRadius: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ px: 1.25, py: 0.4, background: 'rgba(34,197,94,0.15)', color: T.success, borderRadius: 6, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{kw.keyword}</Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 0.4, flexWrap: 'wrap' }}>
                  <ImpactBadge value={kw.strength} />
                  <Typography sx={{ fontSize: 11, color: T.textSecondary }}>{kw.appearances} appearances</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{kw.context}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {discoveryKeywords.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.primary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Discovery Opportunities</Typography>
          {discoveryKeywords.map((kw, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(249,115,22,0.07)', border: `1px solid rgba(249,115,22,0.25)`, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.text }}>{kw.keyword}</Typography>
                <ImpactBadge value={kw.opportunity} />
                <Typography sx={{ fontSize: 11, color: T.textSecondary }}>Volume: {kw.searchVolume}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{kw.reasoning}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {keywordGaps.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.warning, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>⚠ Keyword Gaps</Typography>
          {keywordGaps.map((gap, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(234,179,8,0.07)', border: `1px solid rgba(234,179,8,0.25)`, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.text }}>{gap.gap}</Typography>
                <ImpactBadge value={gap.impact} />
              </Box>
              <Typography sx={{ fontSize: 11, color: T.textSecondary }}>Found in: {gap.foundIn.join(', ')} · Missing from: {gap.missingFrom.join(', ')}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {actionableSuggestions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Action Plan</Typography>
          {actionableSuggestions.map((s, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1, display: 'flex', gap: 1.5 }}>
              <Box sx={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: T.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, mt: 0.25 }}>{i + 1}</Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.action}</Typography>
                  <ImpactBadge value={s.expectedImpact} />
                </Box>
                <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{s.implementation}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <WhyThisMatters text="Keywords help podcasts surface in search results and get recommended to new listeners." citations={data.expertCitations} />
    </Box>
  );
}
